/**
 * WallSketch property getters and registration - NON-HOOK VERSION FOR POTREE
 *
 * This file contains only framework-agnostic code (no React hooks, no Apollo).
 * It can be safely imported by potree_fork without pulling in React dependencies.
 *
 * For React hook versions, see WallSketchProperties.ts
 */

import { PropertyType, PropertyValueType, AnnotationType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

/**
 * Register skeleton properties in the registry
 * Call this once at app initialization
 */
export function registerWallSketchProperties() {
  // `skeleton_skinning_enabled` was removed: 2D skinning is now always on
  // (transparent at opacity 0.4) so there's no user-facing toggle.

  // Toggles the skeleton graph overlay — the node markers and edge lines drawn
  // on top of the wall skin. Off gives a clean floor plan (wall fill + door/
  // window glyphs only), useful for the plan view.
  if (!propertyRegistry.isRegistered('skeleton_overlay_visible')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_overlay_visible',
      name: 'Show skeleton',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Show the skeleton node markers and edge lines',
      ui: {
        tooltip: 'Toggle the skeleton graph overlay (nodes + edges) on top of the wall skin',
        group: 'Wall Visualization',
        order: 1,
      },
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_skinning_3d')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_skinning_3d',
      name: '3D Walls',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Extrude wall polygons into 3D',
      // `ui.hidden` keeps the property reactive + persisted but excludes it
      // from the skeleton properties panel. The skeleton's own 3D toggle was
      // removed in favour of the floor-level 3D toggle (Floor3DToggle), which
      // drives this property for every skeleton in the floor.
      ui: {
        tooltip: 'Extrude wall polygons vertically to create 3D walls',
        group: 'Wall Visualization',
        order: 2,
        hidden: true,
      },
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_skinning_2d_color')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_skinning_2d_color',
      name: 'Wall Color',
      valueType: PropertyValueType.COLOR,
      defaultValue: '#808080',
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Fill color for the walls (2D and 3D)',
      ui: {
        tooltip: 'Color of the walls (filled polygons in 2D, extruded meshes in 3D)',
        group: 'Wall Visualization',
        order: 2,
      },
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_skinning_2d_outline_color')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_skinning_2d_outline_color',
      name: '2D Outline Color',
      valueType: PropertyValueType.COLOR,
      defaultValue: '#000000',
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Outline color for 2D wall polygons',
      ui: {
        tooltip: 'Color of the wall polygon outlines in 2D mode',
        group: 'Wall Visualization',
        order: 3,
      },
    });
  }

  // Heatmap properties (SDF-based distance gradient).
  // MOVED TO THE FLOOR PANEL: the heatmap + clipping controls were
  // consolidated into the floor's `floor_heatmap_*` properties with a
  // rooms / wall-sketch source dropdown — the same consolidation as the 3D
  // toggle (`skeleton_skinning_3d` above). `ui.hidden` keeps these
  // registered (stored project data still references the keys) but out of
  // the wall sketch panel; nothing reads them anymore — the skeleton SDF
  // pipeline follows the floor's properties (see
  // PointoramaWallSketch.loadReactiveProperties).
  if (!propertyRegistry.isRegistered('skeleton_sdf_enabled')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_sdf_enabled',
      name: 'Wall Distance Heatmap',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Color point cloud based on distance to walls',
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_sdf_gradient')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_sdf_gradient',
      name: 'Gradient Type',
      valueType: PropertyValueType.GRADIENT,
      defaultValue: 'SPECTRAL',
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Gradient type for distance coloring',
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_sdf_max_distance')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_sdf_max_distance',
      name: 'Max Distance',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.02,
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Maximum distance for gradient (meters)',
      validation: {
        min: 0.001,
        max: 50.0,
        step: 0.5,
      },
      ui: { hidden: true },
    });
  }


  // SDF clip properties — hidden for the same reason as the heatmap block
  // above: consolidated into the floor's `floor_heatmap_clip_*` properties.
  if (!propertyRegistry.isRegistered('skeleton_sdf_clip_enabled')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_sdf_clip_enabled',
      name: 'Point Cloud Clipping',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Enable point cloud clipping based on distance to walls',
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_sdf_clip_method')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_sdf_clip_method',
      name: 'Clip Method',
      valueType: PropertyValueType.SELECT,
      defaultValue: 'SHOW_INSIDE',
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Clip points based on distance to walls',
      validation: {
        options: [
          { value: 'SHOW_INSIDE', label: 'Isolate' },
          { value: 'SHOW_OUTSIDE', label: 'Hide' },
        ],
      },
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_sdf_clip_tolerance')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_sdf_clip_tolerance',
      name: 'Clip Tolerance',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.01,
      annotationTypes: [AnnotationType.WALL_SKETCH],
      description: 'Distance from walls for clipping (meters)',
      validation: {
        min: 0.001,
        max: 20.0,
        step: 0.1,
      },
      ui: { hidden: true },
    });
  }
}

// ============================================================================
// NON-HOOK HELPERS FOR POTREE (uses global property service)
// ============================================================================

/**
 * Get reactive property for skeleton overlay (node markers + edge lines)
 * visibility (non-hook version for Potree)
 */
export function getWallSketchOverlayVisibleProperty(wallSketchId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_overlay_visible', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

/**
 * Get reactive property for skeleton skinning 3D mode (non-hook version for Potree)
 */
export function getWallSketchSkinning3DProperty(wallSketchId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_skinning_3d', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

/**
 * Get reactive property for skeleton 2D fill color (non-hook version for Potree)
 */
export function getWallSketchSkinning2DColorProperty(wallSketchId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_skinning_2d_color', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/**
 * Get reactive property for skeleton 2D outline color (non-hook version for Potree)
 */
export function getWallSketchSkinning2DOutlineColorProperty(wallSketchId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_skinning_2d_outline_color', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/**
 * Get reactive property for skeleton SDF gradient enabled (non-hook version for Potree)
 */
export function getWallSketchSDFEnabledProperty(wallSketchId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_sdf_enabled', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

/**
 * Get reactive property for skeleton SDF gradient type (non-hook version for Potree)
 */
export function getWallSketchSDFGradientProperty(wallSketchId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_sdf_gradient', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/**
 * Get reactive property for skeleton SDF max distance (non-hook version for Potree)
 */
export function getWallSketchSDFMaxDistanceProperty(wallSketchId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_sdf_max_distance', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}


/**
 * Get reactive property for skeleton SDF clip enabled (non-hook version for Potree)
 */
export function getWallSketchSDFClipEnabledProperty(wallSketchId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_sdf_clip_enabled', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

/**
 * Get reactive property for skeleton SDF clip method (non-hook version for Potree)
 */
export function getWallSketchSDFClipMethodProperty(wallSketchId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_sdf_clip_method', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/**
 * Get reactive property for skeleton SDF clip tolerance (non-hook version for Potree)
 */
export function getWallSketchSDFClipToleranceProperty(wallSketchId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('skeleton_sdf_clip_tolerance', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

// Register properties immediately when module loads
registerWallSketchProperties();
